import { useEffect, useRef, useState } from 'react';
import {
  ProForm,
  ProFormCheckbox,
  ProFormItem,
  ProTable,
  PageContainer,
} from '@ant-design/pro-components';
import {
  Card,
  Form,
  InputNumber,
  Modal,
  Space,
  Tag,
  message,
  Badge,
  Button,
  Popover,
  Typography,
} from 'antd';
import {
  createSalaryIncreaseApproval,
  getBackPayEligibleLessons,
  prepareNextSalaryIncreaseApproval,
  updateSalaryIncreaseApprovalStatus,
  getSalaryIncreaseApprovals,
} from './service';
import CustomDatePicker from '@/components/FormItems/DatePicker';
import { formatMinuteIntToHourMinuteText } from '@/utils/utils';
import { roundToTwo } from '@/utils/AttributeExchange';
import TeacherInfo from '@/components/TeacherInfo/TeacherInfo';
import { CheckOutlined, CloseOutlined, SendOutlined, DeleteOutlined } from '@ant-design/icons';
import {
  getMinutesToNextBracket,
  getLatestSalaryAllocation,
  getBracket,
  getBracketHourlyRate,
  SalaryBracketNames,
} from '@/utils/SalaryTierTransfer';
import { useModel } from '@umijs/max';

const SalaryIncreaseApproval = ({ status, onFinish }) => {
  const [approvalMadalOpen, setApprovalMadalOpen] = useState(false);
  const [approvalData, setApprovalData] = useState({});
  const [lessonList, setLessonList] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [durationPopoverOpen, setDurationPopoverOpen] = useState(null);

  const {
    initialState: { hasPermission },
  } = useModel('@@initialState');

  const actionRef = useRef();

  const onModalCancel = () => {
    setApprovalMadalOpen(false);
    setApprovalData({});
    setLessonList([]);
    setSelectedKeys([]);
  };

  const salaryIncreaseConfirm = (nextSalayLevelData) => {
    Modal.confirm({
      title: '下一阶段升薪提醒',
      content: '检测到老师满足下一阶段升薪,是否发起下一次升薪申请？',
      okText: '确认',
      cancelText: '取消',
      onOk: async () => {
        await createSalaryIncreaseApproval(nextSalayLevelData);
        message.success('发起下一次升薪申请成功');
      },
    });
  };

  const rowSelection = {
    type: 'checkbox',
    preserveSelectedRowKeys: true,
    selectedRowKeys: selectedKeys,
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectedKeys(selectedRowKeys);
    },
  };

  const lessonColumns = [
    {
      title: '课名',
      dataIndex: 'name',
      key: 'name',
      width: 240,
    },
    {
      title: '上课时间',
      dataIndex: 'startAt',
      key: 'startAt',
      valueType: 'dateTime',
    },
    {
      title: '工资时长',
      dataIndex: 'rawDuration',
      key: 'rawDuration',
      render: (value) => {
        const duration = Math.floor((value || 0) / 5) * 5;
        return <span>{formatMinuteIntToHourMinuteText(duration)}</span>;
      },
    },
    {
      title: '已结算工资',
      dataIndex: 'salaryPayment',
      key: 'salaryPayment',
      render: (value) => (
        <span>
          {value?.amount} {value?.currency}
        </span>
      ),
    },
    {
      title: '建议补钱时长',
      dataIndex: ['backPayData', 'backPayMinutes'],
      key: 'backPayMinutes',
      render: (value) => <span>{formatMinuteIntToHourMinuteText(value)}</span>,
    },
    {
      title: '补贴差价金额',
      dataIndex: 'backPayData',
      key: 'backPayData',
      render: (value) => {
        if (!value || value === '-') {
          return null;
        }
        if (value.error) {
          return <span style={{ color: 'red' }}>{value.error}</span>;
        }
        return (
          <div>
            <span style={{ color: '#999' }}>
              应发{value.newPaidAmount} | 实发{value.oldPaidAmount}
            </span>
            <div
              style={{
                color:
                  value.backPayAmount === 0
                    ? '#999'
                    : value.backPayAmount > 0
                      ? '#52c41a'
                      : '#f5222d',
              }}
            >
              + {value.backPayAmount} {value.currency}
            </div>
            {value.lessonSchoolYear && (
              <div style={{ fontSize: '11px', color: '#999' }}>
                年级组: {value.lessonSchoolYear}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  const columns = [
    {
      title: '老师姓名',
      dataIndex: 'name',
      key: 'name',
      render: (_, record) => {
        return <TeacherInfo teacherInfo={record.user} title={'老师信息'} />;
      },
    },
    {
      title: '档位变化',
      dataIndex: 'salaryLevelChange',
      key: 'salaryLevelChange',
      search: false,
      render: (_, record) => {
        return (
          <span>
            {record.currentSalaryLevel} → {record.targetSalaryLevel}
          </span>
        );
      },
    },
    {
      title: '最高时薪变化',
      dataIndex: 'maxHourlyRateChange',
      key: 'maxHourlyRateChange',
      search: false,
      render: (_, record) => {
        return (
          <span>
            {parseInt(record.currentAmount)} → {parseInt(record.targetAmount)}(
            {record.targetCurrency})
          </span>
        );
      },
    },
    {
      title: '升档所需课时',
      dataIndex: 'duration',
      key: 'duration',
      search: false,
      render: (_, record) => {
        if (record.approvalStatus !== '已拒绝') {
          return formatMinuteIntToHourMinuteText(record.duration);
        }
        return (
          <span>
            <Typography.Text delete>
              {formatMinuteIntToHourMinuteText(record.duration)}
            </Typography.Text>
            {formatMinuteIntToHourMinuteText(record.targetDuration)}
          </span>
        );
      },
    },
    {
      title: '满足升档条件时间',
      dataIndex: 'approvalStartTime',
      key: 'approvalStartTime',
      valueType: 'dateRange',
      render: (_, record) => {
        return new Date(record.approvalStartTime).toLocaleString('zh-CN');
      },
    },
    //TODO:增加补贴金额
    {
      title: '审批状态',
      dataIndex: 'approvalStatus',
      key: 'approvalStatus',
      valueType: 'select',
      search: !status,
      valueEnum: {
        审批中: '审批中',
        已通过: '已通过',
        已拒绝: '已拒绝',
      },
      render: (_, record) => {
        //根据status判断
        if (record.approvalStatus === '未发起') {
          return <Badge status="default" text="未发起" />;
        } else if (record.approvalStatus === '审批中') {
          return <Badge status="processing" text="审批中" />;
        } else if (record.approvalStatus === '已通过') {
          return <Badge status="success" text="已通过" />;
        } else if (record.approvalStatus === '已拒绝') {
          return <Badge status="error" text="已拒绝" />;
        }
      },
    },
  ];
  if (!status) {
    columns.push(
      {
        title: '补贴总金额',
        dataIndex: 'totalSubsidyAmount',
        key: 'totalSubsidyAmount',
        search: false,
        render: (_, record) => {
          if (!record.subsidyAmount) {
            return <span>-</span>;
          }
          return (
            <span>
              {record.subsidyAmount}
              {`(${record.targetCurrency})`}
            </span>
          );
        },
      },
      {
        title: '补贴总时长',
        dataIndex: 'totalSubsidyDuration',
        key: 'totalSubsidyDuration',
        search: false,
        render: (_, record) => {
          if (!record.subsidyDuration) {
            return <span>-</span>;
          }
          return <span>{formatMinuteIntToHourMinuteText(record.subsidyDuration)}</span>;
        },
      },
    );
  }

  columns.push({
    title: '操作',
    dataIndex: 'option',
    valueType: 'option',
    width: 120,
    render: (_, record) => {
      if (
        !hasPermission({
          route: 'salaryIncreaseApprovals',
          function: 'update',
        }) ||
        record.approvalStatus === '已拒绝' ||
        record.approvalStatus === '已通过'
      ) {
        return null;
      }
      const buttonList = [];
      const latestAllocation = getLatestSalaryAllocation(record.user.salaryAllocations);
      if (!latestAllocation) {
        return null;
      }
      const currentSalaryLevel = getBracket(latestAllocation.currency, latestAllocation.amount);
      let abnormalMessage = '';
      if (!currentSalaryLevel) {
        abnormalMessage = `当前薪酬方案(${latestAllocation.amount} ${latestAllocation.currency})不在标准档位里`;
      }
      const idx_now = SalaryBracketNames.indexOf(currentSalaryLevel);
      const idx_future = SalaryBracketNames.indexOf(record.targetSalaryLevel);
      if (idx_now >= idx_future) {
        abnormalMessage = `已存在【${currentSalaryLevel}】薪酬方案`;
      }
      if (abnormalMessage) {
        return (
          <div>
            <Button
              type="link"
              size="small"
              onClick={async () => {
                try {
                  await updateSalaryIncreaseApprovalStatus({
                    id: record.id,
                    approvalStatus: '已删除',
                  });
                  onFinish?.();
                  actionRef.current?.reload();
                  message.success('删除成功');
                } catch {
                  message.error('删除失败');
                }
              }}
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
            <span style={{ color: 'red', fontSize: 12 }}>{abnormalMessage}</span>
          </div>
        );
      }
      if (record.approvalStatus === '未发起') {
        buttonList.push(
          <Button
            size="small"
            type="link"
            key="initiate"
            icon={<SendOutlined />}
            onClick={async () => {
              try {
                await updateSalaryIncreaseApprovalStatus({
                  id: record.id,
                  approvalStatus: '审批中',
                });
                onFinish?.();
                actionRef.current?.reload();
                message.success('发起审批成功');
              } catch {
                message.error('发起审批失败');
              }
            }}
          >
            发起审批
          </Button>,
        );
      }
      if (record.approvalStatus === '审批中') {
        buttonList.push(
          <Button
            size="small"
            type="link"
            key="approve"
            disabled={record.approvalStatus !== '审批中'}
            onClick={() => {
              setApprovalData({
                id: record.id,
                validFrom: record.approvalStartTime,
                salaryLevel: record.targetSalaryLevel,
                currentAmount: getBracketHourlyRate(
                  record.targetSalaryLevel,
                  record.user.salaryCurrency,
                ),
                unit: '小时',
                currency: record.user.salaryCurrency,
                category: '一对一',
                type: '学术辅导',
                teacherId: record.user.id,
                //之前的老师挡位
                previousSalaryLevel: record.currentSalaryLevel,
                approvalStartTime: record.approvalStartTime,
                //之前的薪资
                previousAmount: latestAllocation.amount,
                //之前的货币
                previousCurrency: latestAllocation.currency,
              });
              setApprovalMadalOpen(true);
            }}
            icon={<CheckOutlined />}
          >
            同意
          </Button>,
        );
      }
      buttonList.push(
        <Popover
          title="更新升档所需总课时，老师累计课时达到该时长后，会再次触发升档审批"
          destroyTooltipOnHide
          open={durationPopoverOpen === record.id}
          content={
            <Form
              destroyOnClose
              layout="inline"
              initialValues={{
                duration: record.duration / 60,
              }}
              onFinish={async (values) => {
                try {
                  await updateSalaryIncreaseApprovalStatus({
                    id: record.id,
                    approvalStatus: '已拒绝',
                    newDuration: values.duration * 60,
                  });
                  onFinish?.();
                  actionRef.current?.reload();
                  message.success('更新升档时长成功');
                  setDurationPopoverOpen(null);
                } catch {
                  message.error('更新升档时长失败');
                }
              }}
            >
              <Form.Item
                name="duration"
                label="升档所需总课时"
                rules={[{ required: true, message: '请输入所需课时' }]}
              >
                <InputNumber min={0} addonAfter="小时" />
              </Form.Item>
              <Form.Item>
                <Button type="primary" htmlType="submit" style={{ marginRight: 10 }}>
                  提交
                </Button>
                <Button onClick={() => setDurationPopoverOpen(null)}>取消</Button>
              </Form.Item>
            </Form>
          }
        >
          <Button
            size="small"
            type="link"
            key="delay"
            icon={<CloseOutlined />}
            onClick={() => {
              setDurationPopoverOpen(record.id);
            }}
          >
            推迟升档
          </Button>
        </Popover>,
      );
      return <div>{buttonList}</div>;
    },
  });

  return (
    <PageContainer>
      <ProTable
        id="salaryIncreaseApprovalTable"
        options={false}
        rowKey="id"
        columns={columns}
        params={{
          approvalStatus: status,
        }}
        request={async (params, sorter, filter) => {
          const res = await getSalaryIncreaseApprovals(params);
          return {
            success: true,
            data: res.data,
            total: res.total,
          };
        }}
        actionRef={actionRef}
        search={{
          labelWidth: 'auto',
          defaultCollapsed: false,
        }}
      />
      <Modal
        title="同意升档"
        width={1000}
        open={approvalMadalOpen}
        destroyOnClose
        okText="确认通过"
        cancelText="取消"
        onCancel={onModalCancel}
        confirmLoading={loading}
        onOk={async () => {
          setLoading(true);
          const totalSubsidyAmount = lessonList.reduce((pre, item) => {
            if (selectedKeys.includes(item.id)) {
              return pre + item.backPayData?.backPayAmount || 0;
            }
            return pre;
          }, 0);
          const totalSubsidyDuration = lessonList.reduce((pre, item) => {
            if (selectedKeys.includes(item.id)) {
              return pre + item.backPayData?.backPayMinutes || 0;
            }
            return pre;
          }, 0);
          const teacherId = approvalData.teacherId;
          try {
            await updateSalaryIncreaseApprovalStatus({
              id: approvalData.id,
              approvalStatus: '已通过',
              subsidyAmount: roundToTwo(totalSubsidyAmount),
              subsidyDuration: totalSubsidyDuration,
              targetSalaryLevel: approvalData.salaryLevel,
              newDuration: getMinutesToNextBracket(approvalData.salaryLevel),
              // 新的薪酬方案
              schoolYears: approvalData.schoolYear,
              amount: approvalData.currentAmount,
              currency: approvalData.currency,
              validFrom: approvalData.validFrom,
            });
            onFinish?.();
            actionRef.current?.reload();
            message.success('审批通过!');
            onModalCancel();
          } catch (error) {
            message.error('更新审批状态失败!');
          } finally {
            setLoading(false);
          }
          try {
            const res = await prepareNextSalaryIncreaseApproval({
              teacherId,
            });
            if (res.status === 'success') {
              salaryIncreaseConfirm(res.data);
            }
          } catch (error) {
            message.error('查询老师是否能再次发起涨薪申请失败!');
          }
        }}
      >
        <Card title="新方案配置" bordered={false} size="small">
          <div style={{ marginBottom: 16 }}>
            <p>
              <strong>基础信息：</strong>【{approvalData.category} | {approvalData.type}】
            </p>
            <p>
              <strong>升档后薪酬挡位：</strong>
              <Tag color="blue">{approvalData.salaryLevel}</Tag>
              {approvalData.currentAmount} {approvalData.currency}/{approvalData.unit}
            </p>
          </div>

          <ProForm
            layout="horizontal"
            submitter={false}
            initialValues={{
              schoolYear: approvalData.schoolYear,
              validFrom: approvalData.validFrom,
            }}
            onValuesChange={(changedValues, allValues) => {
              if (changedValues.schoolYear || changedValues.validFrom) {
                setApprovalData((prev) => ({
                  ...prev,
                  ...changedValues,
                }));
              }
            }}
          >
            <ProFormItem
              label="薪酬方案生效时间"
              name="validFrom"
              rules={[{ required: true, message: '请选择生效时间' }]}
              style={{ width: '75%' }}
            >
              <CustomDatePicker />
            </ProFormItem>
            <ProFormCheckbox.Group
              label="选择涨薪年级组合"
              name="schoolYear"
              options={[
                { label: '高中&大学预科', value: '高中&大学预科' },
                { label: '大一&大二', value: '大一&大二' },
                { label: '大三&大四&研究生预科', value: '大三&大四&研究生预科' },
                { label: '研究生', value: '研究生' },
              ]}
              extra="请明确选择要创建新薪酬方案的年级组合。仅选中的组合会获得涨薪，未选中的组合将保持现有薪酬不变。"
            />
          </ProForm>

          {approvalData.schoolYear && approvalData.schoolYear.length > 0 && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: 6,
              }}
            >
              <p style={{ margin: 0, color: '#52c41a', fontWeight: 'bold' }}>
                ✓ 将为以下年级组合创建新薪酬方案：{approvalData.schoolYear.join(', ')}
              </p>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#666' }}>
                只有属于选中年级组合的课节才会获得补偿金额，其他课节保持原薪酬不变
              </p>
            </div>
          )}

          {(!approvalData.schoolYear || approvalData.schoolYear.length === 0) && (
            <div
              style={{
                marginTop: 16,
                padding: 12,
                backgroundColor: '#fff2e8',
                border: '1px solid #ffcb7d',
                borderRadius: 6,
              }}
            >
              <p style={{ margin: 0, color: '#fa8c16', fontWeight: 'bold' }}>
                ⚠️ 未选择任何年级组合，将不会创建新的薪酬方案
              </p>
            </div>
          )}
        </Card>
        <Card title="建议需要补钱的课节" bordered={false} size="small">
          <ProTable
            size="small"
            options={false}
            id="salaryIncreaseApprovalCourseList"
            rowKey="id"
            search={false}
            params={{
              teacherId: approvalData.teacherId,
              selectedSchoolYears: approvalData.schoolYear || [],
              approvalStartTime: approvalData.validFrom,
              newSalaryRate: approvalData.currentAmount,
              newSalaryCurrency: approvalData.currency,
            }}
            request={async (params) => {
              if (!approvalData.teacherId) {
                return {
                  data: [],
                  total: 0,
                  success: true,
                };
              }
              const { data, total } = await getBackPayEligibleLessons(params);
              setSelectedKeys(
                data.filter((item) => item.backPayData?.backPayAmount !== 0).map((item) => item.id),
              );
              setLessonList(data);
              return {
                data,
                total,
                success: true,
              };
            }}
            rowSelection={rowSelection}
            tableAlertRender={({ selectedRowKeys, selectedRows, onCleanSelected }) => {
              const totalSubsidyDuration = selectedRows.reduce((pre, item) => {
                return pre + item.backPayData?.backPayMinutes || 0;
              }, 0);
              const totalSubsidyAmount = selectedRows.reduce((pre, item) => {
                return pre + item.backPayData?.backPayAmount || 0;
              }, 0);

              return (
                <Space size={24}>
                  <span>
                    已选 {selectedRowKeys.length} 项
                    <a style={{ marginInlineStart: 8 }} onClick={onCleanSelected}>
                      全部取消
                    </a>
                  </span>
                  <span>
                    补贴时长{formatMinuteIntToHourMinuteText(totalSubsidyDuration)}，总额{' '}
                    <span style={{ color: '#52c41a' }}>
                      {roundToTwo(totalSubsidyAmount)} {approvalData.currency}
                    </span>
                  </span>
                </Space>
              );
            }}
            tableAlertOptionRender={false}
            columns={lessonColumns}
          />
        </Card>
      </Modal>
    </PageContainer>
  );
};

export default SalaryIncreaseApproval;
